'use client';

import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface WellnessTrendsProps {
  contactId: string;
  refreshTrigger: number;
}

const WellnessTrends = ({ contactId, refreshTrigger }: WellnessTrendsProps) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [visibleLines, setVisibleLines] = useState({
    wellness: true,
    cognitive: true,
    visual: true,
    medication: true,
  });
  const [sparklineData, setSparklineData] = useState({
    pain: { avg: 0, trend: 'stable', data: [] as number[] },
    mood: { avg: 0, trend: 'stable', data: [] as number[] },
    completion: { avg: 0, trend: 'up', data: [] as number[] },
  });

  useEffect(() => {
    fetchTrendData();
  }, [contactId, refreshTrigger]);

  const fetchTrendData = async () => {
    setLoading(true);
    const supabase = createClient();

    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Fetch check-in calls
      const { data: calls, error } = await supabase
        .from('check_in_calls')
        .select('*')
        .eq('contact_id', contactId)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Process data for chart
      const processedData = calls?.map((call: any) => {
        const date = new Date(call.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
        const responseData = call.response_data || {};
        return {
          date,
          wellness: call.wellness_score || 0,
          cognitive: call.cognitive_score || 0,
          visual: call.visual_score || 0,
          medication: call.medications_taken ? 100 : 0,
          pain: responseData.pain_level || 0,
          mood: call.mood === 'good' ? 80 : call.mood === 'neutral' ? 60 : 40,
        };
      }) || [];

      setChartData(processedData);

      // Calculate sparkline data
      const last7Days = processedData.slice(-7);
      const last14Days = processedData.slice(-14);

      const painAvg = last7Days.reduce((sum, d) => sum + d.pain, 0) / last7Days.length;
      const moodAvg = last7Days.reduce((sum, d) => sum + d.mood, 0) / last7Days.length;
      const completionRate =
        (last14Days.filter((d) => d.wellness > 0).length / last14Days.length) * 100;

      setSparklineData({
        pain: {
          avg: painAvg,
          trend: painAvg < 4 ? 'down' : 'stable',
          data: last7Days.map((d) => d.pain),
        },
        mood: {
          avg: moodAvg,
          trend: 'stable',
          data: last7Days.map((d) => d.mood),
        },
        completion: {
          avg: completionRate,
          trend: completionRate > 90 ? 'up' : 'stable',
          data: last14Days.map((d) => (d.wellness > 0 ? 100 : 0)),
        },
      });
    } catch (error) {
      console.error('Error fetching trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLine = (line: keyof typeof visibleLines) => {
    setVisibleLines((prev) => ({ ...prev, [line]: !prev[line] }));
  };

  if (loading) {
    return (
      <div className="glass p-6 animate-pulse">
        <div className="h-6 bg-sage-200/30 rounded w-1/3 mb-4"></div>
        <div className="h-64 bg-sage-200/30 rounded"></div>
      </div>
    );
  }

  return (
    <div className="glass">
      <div className="p-6 border-b border-sage-200/30">
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-1">
          Wellness Trends
        </h2>
        <p className="text-sm text-muted-foreground">30-day longitudinal analysis</p>
      </div>

      <div className="p-6">
        {/* Line Toggles */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => toggleLine('wellness')}
            className={`px-4 py-2 rounded-lg border transition-smooth ${
              visibleLines.wellness
                ? 'bg-[#7FB069]/10 border-[#7FB069] text-[#7FB069]'
                : 'bg-background border-sage-200/30 text-muted-foreground'
            }`}
          >
            Overall Wellness
          </button>
          <button
            onClick={() => toggleLine('cognitive')}
            className={`px-4 py-2 rounded-lg border transition-smooth ${
              visibleLines.cognitive
                ? 'bg-blue-500/10 border-blue-500 text-blue-500' :'bg-background border-sage-200/30 text-muted-foreground'
            }`}
          >
            Cognitive Score
          </button>
          <button
            onClick={() => toggleLine('visual')}
            className={`px-4 py-2 rounded-lg border transition-smooth ${
              visibleLines.visual
                ? 'bg-purple-500/10 border-purple-500 text-purple-500' :'bg-background border-sage-200/30 text-muted-foreground'
            }`}
          >
            Visual Score
          </button>
          <button
            onClick={() => toggleLine('medication')}
            className={`px-4 py-2 rounded-lg border transition-smooth ${
              visibleLines.medication
                ? 'bg-[#E8A838]/10 border-[#E8A838] text-[#E8A838]'
                : 'bg-background border-sage-200/30 text-muted-foreground'
            }`}
          >
            Medication Compliance
          </button>
        </div>

        {/* Main Chart */}
        <div className="w-full h-80 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: '12px' }} />
              <YAxis domain={[0, 100]} stroke="#6b7280" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontSize: '12px' }} iconType="line" />
              {visibleLines.wellness && (
                <Line
                  type="monotone"
                  dataKey="wellness"
                  stroke="#7FB069"
                  strokeWidth={3}
                  name="Overall Wellness"
                  dot={{ fill: '#7FB069', r: 3 }}
                />
              )}
              {visibleLines.cognitive && (
                <Line
                  type="monotone"
                  dataKey="cognitive"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Cognitive"
                  dot={{ fill: '#3b82f6', r: 3 }}
                />
              )}
              {visibleLines.visual && (
                <Line
                  type="monotone"
                  dataKey="visual"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  name="Visual"
                  dot={{ fill: '#8b5cf6', r: 3 }}
                />
              )}
              {visibleLines.medication && (
                <Line
                  type="monotone"
                  dataKey="medication"
                  stroke="#E8A838"
                  strokeWidth={2}
                  name="Medication"
                  dot={{ fill: '#E8A838', r: 3 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Sparkline Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-background/50 rounded-lg p-4 border border-sage-200/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Pain Level (7-day avg)</span>
              <span className="text-xs text-error">↓</span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-2">
              {sparklineData.pain.avg.toFixed(1)}/10
            </div>
            <div className="h-8">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparklineData.pain.data.map((v, i) => ({ value: v }))}>
                  <Area type="monotone" dataKey="value" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-background/50 rounded-lg p-4 border border-sage-200/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Mood Score (7-day)</span>
              <span className="text-xs text-muted-foreground">→</span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-2">
              {Math.round(sparklineData.mood.avg)}/100
            </div>
            <div className="h-8">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparklineData.mood.data.map((v, i) => ({ value: v }))}>
                  <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-background/50 rounded-lg p-4 border border-sage-200/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Call Completion (14-day)</span>
              <span className="text-xs text-success">↑</span>
            </div>
            <div className="text-2xl font-bold text-foreground mb-2">
              {Math.round(sparklineData.completion.avg)}%
            </div>
            <div className="h-8">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparklineData.completion.data.map((v, i) => ({ value: v }))}>
                  <Area type="monotone" dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Insight Box */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <Icon name="ChartBarIcon" size={24} className="text-primary flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-foreground mb-2">
                📊 February Analysis
              </h3>
              <p className="text-sm text-foreground leading-relaxed">
                Margaret's cognitive scores have been stable this month with one dip during Feb
                7-9. Medication compliance improved in the last week. Pain levels remain
                well-managed. Overall trajectory: <span className="font-semibold">Stable with monitoring advised</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessTrends;